import React, { useState } from "react";
import { PostFieldLabel, PlatformUsage } from "components/Posts/PostForm";
import { FIELDS, GMB_TOPICS } from "constants/PostFormFields";
import DateTimePicker from "components/DateTimePicker/DateTimePicker";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { format, addDays, endOfDay, isBefore } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import PostFieldError from "components/Posts/PostForm/PostFieldError/PostFieldError";
import { useSelector } from "react-redux";
import { getUser } from "selectors/commonSelectors";

const PostEventDates = ({
  topicType,
  post,
  selectedPlatform,
  platforms,
  disabled = false,
  onChange,
  className = "",
  errors,
}) => {
  const forTopics = [GMB_TOPICS.OFFER.VALUE, GMB_TOPICS.EVENT.VALUE];
  if (!forTopics.includes(topicType)) {
    return "";
  }

  const user = useSelector(getUser);

  const { gmbEvent } = post;
  const [startDate, setStartDate] = useState(
    gmbEvent && gmbEvent.startDate
      ? toZonedTime(new Date(gmbEvent.startDate), user.timezone)
      : null
  );
  const [endDate, setEndDate] = useState(
    gmbEvent && gmbEvent.endDate
      ? toZonedTime(new Date(gmbEvent.endDate), user.timezone)
      : null
  );
  const now = new Date();
  const startInThePast = startDate ? isBefore(startDate, now) : false;
  const endInThePast = endDate ? isBefore(endDate, now) : false;

  return (
    <div className={className}>
      <div className="flex items-center">
        <PostFieldLabel className="mb-2 mr-4">
          {topicType === GMB_TOPICS.OFFER.VALUE ? "Offer" : "Event"} dates
          (required)
        </PostFieldLabel>
        <PlatformUsage
          platforms={platforms}
          field={FIELDS.GMB_START_DATE}
          selectedPlatform={selectedPlatform}
        />
      </div>
      <div className="flex">
        <EventDate
          className="mb-4 mr-4"
          date={startDate}
          onChange={(newDate) => {
            setStartDate(toZonedTime(new Date(newDate), user.timezone));
            onChange({ [FIELDS.GMB_START_DATE]: newDate });
          }}
          inThePast={startInThePast}
          disabled={disabled}
        >
          Start date
        </EventDate>
        <EventDate
          className=""
          date={endDate}
          onChange={(newDate) => {
            setEndDate(toZonedTime(new Date(newDate), user.timezone));
            onChange({ [FIELDS.GMB_END_DATE]: newDate });
          }}
          inThePast={endInThePast}
          disabled={disabled}
        >
          End date
        </EventDate>
      </div>
      <PostFieldError
        className="mt-4"
        fields={[FIELDS.GMB_START_DATE, FIELDS.GMB_END_DATE]}
        errors={errors}
      />
    </div>
  );
};

const EventDate = ({ date, children, onChange, inThePast, className }) => {
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const scheduledClasses = "border-blue-500 bg-blue-200 text-blue-500";
  const scheduledErrorClasses = "border-red-500 bg-red-100 text-red-500";

  const timePreference = "12h";
  // const dateFormat = "d MMM hh:mma";
  const dateFormat = timePreference === "12h" ? "d MMM hh:mma" : "d MMM HH:mm";
  const disabled = false;
  const maxSelectableDate = endOfDay(addDays(new Date(), 364));

  return (
    <div className={className}>
      <DisplayBox
        className={`p-4 ${
          inThePast ? scheduledErrorClasses : scheduledClasses
        }`}
      >
        {children}:
        <Popover open={datePickerVisible} onOpenChange={setDatePickerVisible}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="ml-4 cursor-pointer border-0 bg-transparent p-0 text-inherit underline"
              disabled={disabled}
            >
              {date ? format(date, dateFormat) : "Pick date"}
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="center"
            sideOffset={8}
            collisionPadding={16}
            widthMode="content"
            className="border-0 bg-transparent p-0 shadow-none"
          >
            <DateTimePicker
              selectedDate={date}
              onChange={(newDateTime) => {
                setDatePickerVisible(false);
                onChange(newDateTime);
              }}
              timePreference={timePreference}
              onClose={() => setDatePickerVisible(false)}
              showTriangle={false}
              maxSelectableDate={maxSelectableDate}
            />
          </PopoverContent>
        </Popover>
      </DisplayBox>
    </div>
  );
};

const DisplayBox = ({ children, className = "" }) => {
  return (
    <div
      className={`flex items-center border border-solid rounded-lg font-medium px-4 ${className}`}
    >
      {children}
    </div>
  );
};

export default PostEventDates;
